import { createApi } from "@reduxjs/toolkit/query/react";
import CustomFetchBase from "../CustomFetchBase";

export const EnquiryApi = createApi({
  reducerPath: "EnquiryApi",
  baseQuery: CustomFetchBase,
  tagTypes: ["Enquiry", "Enquiries"],
  endpoints: (builder) => ({
    // Create new enquiry (Public)
    createEnquiry: builder.mutation({
      query: (data) => ({
        url: "/enquiries",
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["Enquiries"],
    }),

    // Get all enquiries with pagination (Admin/Manager)
    getAllEnquiries: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        status = "",
        industry = "",
        priority = "",
      } = {}) => ({
        url: `/enquiries?page=${page}&limit=${limit}&search=${search}&status=${status}&industry=${industry}&priority=${priority}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["Enquiries"],
    }),

    // Get enquiry by ID
    getEnquiryById: builder.query({
      query: (id) => ({
        url: `/enquiries/${id}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: (result, error, id) => [{ type: "Enquiry", id }],
    }),

    // Update enquiry
    updateEnquiry: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/enquiries/${id}`,
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Enquiry", id },
        "Enquiries",
      ],
    }),

    // Edit enquiry (partial update)
    editEnquiry: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/enquiries/${id}`,
        method: "PATCH",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Enquiry", id },
        "Enquiries",
      ],
    }),

    // Delete enquiry (Admin only)
    deleteEnquiry: builder.mutation({
      query: (id) => ({
        url: `/enquiries/${id}`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["Enquiries"],
    }),

    // Add note to enquiry
    addNoteToEnquiry: builder.mutation({
      query: ({ id, text }) => ({
        url: `/enquiries/${id}/notes`,
        method: "POST",
        body: { text },
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Enquiry", id },
        "Enquiries",
      ],
    }),

    // Get enquiry statistics
    getEnquiryStats: builder.query({
      query: () => ({
        url: `/enquiries/stats`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["Enquiries"],
    }),
  }),
});

export const {
  useCreateEnquiryMutation,
  useGetAllEnquiriesQuery,
  useGetEnquiryByIdQuery,
  useUpdateEnquiryMutation,
  useEditEnquiryMutation,
  useDeleteEnquiryMutation,
  useAddNoteToEnquiryMutation,
  useGetEnquiryStatsQuery,
  useLazyGetAllEnquiriesQuery,
  useLazyGetEnquiryByIdQuery,
} = EnquiryApi;
